import React, { type ReactElement } from 'react';
import { Icon, InlineMessage, InputField } from '@saviynt/design-system';
import cx from 'classnames';

import { FieldLabel } from '../../../../shared/components';
import { testIds, VALIDATION_TYPES } from '../../../../shared/constants';
import { type TextField } from '../../../../shared/types';
import { HELPTEXT_VALIDATION } from '../../../FormBuilder/data-layer/validation/validationRules';

import styles from './PreviewFields.module.css';

interface TextPreviewProps {
  field: TextField;
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  isRequired?: boolean;
  isReadOnly?: boolean;
}

/**
 * TextInput component for rendering short text fields in form preview
 */
function TextInput({
  field,
  value,
  onChange,
  error,
  isRequired: isRequiredProp,
  isReadOnly: isReadOnlyProp,
}: TextPreviewProps): ReactElement {
  const {
    fieldName,
    label,
    placeholder,
    helperText,
    infoTooltipText,
    validations,
    isReadOnly: staticReadOnly,
  } = field;

  // Use prop if provided, otherwise fallback to validation rules
  const isRequired =
    isRequiredProp ??
    validations?.some((rule) => rule.type === VALIDATION_TYPES.REQUIRED) ??
    false;
  const isReadOnly = staticReadOnly || isReadOnlyProp || false;

  return (
    <InputField
      id={fieldName}
      name={fieldName}
      size='medium'
      placeholder={placeholder ?? ''}
      value={value}
      setValue={(v: string) => onChange?.(v)}
      label={
        <FieldLabel
          label={label}
          htmlFor={fieldName}
          isReadOnly={isReadOnly}
          infoTooltipText={infoTooltipText}
        />
      }
      isRequired={isRequired}
      isDisabled={isReadOnly}
      tabIndex={0}
      isCritical={!!error}
      CriticalHelperText={
        <InlineMessage
          text={error}
          colorTheme='critical'
          size='small'
          leftIcon={<Icon kind='AlertCriticalFilled' />}
        />
      }
      HelperText={
        helperText ? (
          <InlineMessage
            text={helperText.slice(0, HELPTEXT_VALIDATION.MAX_LENGTH)}
            colorTheme='neutral'
            size='small'
          />
        ) : undefined
      }
      className={cx(isReadOnly && styles.value__isReadOnly)}
      dataTestId={testIds.FF_PREVIEW_TEXT_INPUT_FIELD}
    />
  );
}

export default TextInput;
